// Step-aside on the processor fee (issue #110): POST /api/finance/processor-fees/step-aside.
// The "I'll handle it myself" secondary action on the armed fee pin. Like the period-scoped
// Record path it carries ONLY the processor identity plus the treasurer's disposition (reason +
// optional note); the server resolves the OWED period itself (never sent from the frontend),
// records a first-class period+processor step-aside + its forensic audit row, and the federal
// must-record gate then proceeds for that period+processor. After a step-aside the armed pin
// disappears (the display lines drop it), so the same three surfaces the Record path touches —
// the sort-inbox ledger, the report preflight, and finance health — are invalidated.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { StepAsideProcessorFeeRequest } from '@signalvote/shared';
import { apiVoid } from './apiFetch';
import { invalidateFeePinQueries } from './feePinInvalidation';

/** Typed error carrying the server's message for a failed step-aside. */
export class StepAsideProcessorFeeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StepAsideProcessorFeeError';
  }
}

/**
 * Step aside on the owed-period processor fee. Sends the processor identity + the treasurer's
 * disposition (reason + optional note); the server resolves the owed period. The sort-inbox
 * surfaces, the report preflight, and finance health all refresh so the armed pin disappears.
 */
export function useStepAsideProcessorFee() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, StepAsideProcessorFeeRequest>({
    mutationFn: ({ processorId, reason, note }) =>
      apiVoid('/api/finance/processor-fees/step-aside', {
        method: 'POST',
        json: { processorId, reason, note },
        error: (error) =>
          new StepAsideProcessorFeeError(error.error ?? 'Failed to step aside on the processor fee'),
      }),
    // Recording and stepping aside refresh the SAME three surfaces (the shared fee-pin set).
    onSuccess: () => invalidateFeePinQueries(queryClient),
  });
}
